import React from "react";
import { FiCheck, FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import { LoadingSpinner } from "../LoadingSpinner";
import { CATEGORY_FILTERS, ensureLeadingSlash, PAGE_SIZE, type PageListItem } from "./hrefInputUtils";
import type { HrefTarget } from "../../types/layout";

type PageBrowserPanelProps = {
	pages: PageListItem[];
	loadingPages: boolean;
	pagesError: string | null;
	pagesPage: number;
	pagesTotal: number;
	search: string;
	categoryFilter: string;
	languageFilter: string;
	languages: string[];
	pendingValue: HrefTarget | null | undefined;
	onSearchChange: (value: string) => void;
	onCategoryFilterChange: (value: string) => void;
	onLanguageFilterChange: (value: string) => void;
	onSelectPage: (page: PageListItem) => void;
	onPageChange: (page: number) => void;
};

export const PageBrowserPanel: React.FC<PageBrowserPanelProps> = ({
	pages,
	loadingPages,
	pagesError,
	pagesPage,
	pagesTotal,
	search,
	categoryFilter,
	languageFilter,
	languages,
	pendingValue,
	onSearchChange,
	onCategoryFilterChange,
	onLanguageFilterChange,
	onSelectPage,
	onPageChange
}) => {
	const totalPages = Math.max(1, Math.ceil(pagesTotal / PAGE_SIZE));
	const canPrev = pagesPage > 1;
	const canNext = pagesPage < totalPages;

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2">
				<div className="relative w-full max-w-xl">
					<FiSearch className="absolute left-3 top-3 text-slate-400" />
					<input
						className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						placeholder="Search pages by slug, title, or description"
						value={search}
						onChange={(e) => {
							onSearchChange(e.target.value);
							onPageChange(1);
						}}
					/>
				</div>
				<select
					value={languageFilter}
					onChange={(e) => {
						onLanguageFilterChange(e.target.value);
						onPageChange(1);
					}}
					className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
				>
					<option value="all">All languages</option>
					{languages.map((lang) => (
						<option key={lang} value={lang}>
							{lang.toUpperCase()}
						</option>
					))}
				</select>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				{CATEGORY_FILTERS.map((filter) => {
					const isActive = filter.key === categoryFilter;
					return (
						<button
							key={filter.key}
							type="button"
							onClick={() => {
								onCategoryFilterChange(filter.key);
								onPageChange(1);
							}}
							className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${
								isActive
									? "border-slate-900 bg-slate-900 text-white shadow-sm"
									: "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
							}`}
						>
							{filter.label}
						</button>
					);
				})}
			</div>

			<div className="relative max-h-[360px] overflow-y-auto rounded-xl border border-slate-200 bg-white/70 p-3">
				{loadingPages && (
					<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
						<LoadingSpinner />
					</div>
				)}
				{pagesError && (
					<div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
						{pagesError}
					</div>
				)}
				{!pagesError && pages.length === 0 && (
					<div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
						No pages match your search.
					</div>
				)}
				<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
					{pages.map((page) => {
						const displaySlug = page.slug ? ensureLeadingSlash(page.slug) : "/page";
						const isCurrent =
							(pendingValue?.pageId && page.id && pendingValue.pageId === page.id) ||
							(pendingValue?.slug &&
								ensureLeadingSlash(pendingValue.slug) === ensureLeadingSlash(displaySlug));
						return (
							<button
								key={`${page.slug}-${page.id}`}
								type="button"
								onClick={() => onSelectPage(page)}
								className={`w-full rounded-lg border bg-white p-3 text-left shadow-sm transition hover:border-slate-300 hover:shadow ${
									isCurrent ? "border-slate-900" : "border-slate-200"
								}`}
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0 space-y-1">
										<p className="text-sm font-semibold text-slate-900 truncate">
											{page.title || displaySlug}
										</p>
										<p className="text-xs text-slate-600 truncate">{displaySlug}</p>
										{page.id && (
											<p className="text-[11px] font-mono text-slate-500 truncate">id: {page.id}</p>
										)}
									</div>
									<FiCheck
										className={`text-emerald-500 transition ${isCurrent ? "opacity-100" : "opacity-0"}`}
										aria-hidden="true"
									/>
								</div>
							</button>
						);
					})}
				</div>
			</div>
			{pagesTotal > 0 && (
				<div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
					<span>
						Showing {(pagesPage - 1) * PAGE_SIZE + 1}–{Math.min(pagesPage * PAGE_SIZE, pagesTotal)} of {pagesTotal}
					</span>
					<div className="flex items-center gap-2">
						<button
							type="button"
							disabled={!canPrev}
							onClick={() => canPrev && onPageChange(Math.max(1, pagesPage - 1))}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<FiChevronLeft /> Prev
						</button>
						<span>
							Page {pagesPage} of {totalPages}
						</span>
						<button
							type="button"
							disabled={!canNext}
							onClick={() => canNext && onPageChange(Math.min(totalPages, pagesPage + 1))}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Next <FiChevronRight />
						</button>
					</div>
				</div>
			)}
		</div>
	);
};
